/**
 * The identity of one login (#436 guide popup): a random id written when the
 * member logs in (`setAuth`) and removed when they log out. Things that should
 * happen "once per login" — not on every reload, tab or token refresh — key on
 * it. Token refreshes rotate the JWTs, so the tokens themselves can't be that
 * key; this id survives them and dies with the session.
 *
 * Stored in localStorage (shared by the login's tabs), falling back to
 * sessionStorage and then memory when storage is blocked. Every access is
 * guarded: a broken storage degrades to "once per tab/page load", never to an
 * error.
 */

export type KeyValueStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const LOGIN_ID_KEY = "portal_login_id";

const memory = new Map<string, string>();

/** Last resort when no web storage works: lives as long as the page. */
export const memoryStore: KeyValueStore = {
  getItem: (k) => memory.get(k) ?? null,
  setItem: (k, v) => void memory.set(k, String(v)),
  removeItem: (k) => void memory.delete(k),
};

/** The first candidate that can write and read back, else `memoryStore`. */
export function pickStore(candidates: Array<() => KeyValueStore | undefined | null>): KeyValueStore {
  const probe = "__nodus_probe__";
  for (const get of candidates) {
    try {
      const store = get();
      if (!store) continue;
      store.setItem(probe, probe);
      const ok = store.getItem(probe) === probe;
      store.removeItem(probe);
      if (ok) return store;
    } catch {
      // blocked (private mode, sandboxed iframe, SecurityError) — try the next
    }
  }
  return memoryStore;
}

/** The browser's store for login-scoped flags. */
export function loginStore(): KeyValueStore {
  return pickStore([() => globalThis.localStorage, () => globalThis.sessionStorage]);
}

export function readKey(store: KeyValueStore, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

export function writeKey(store: KeyValueStore, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    // full or blocked: the flag just doesn't stick
  }
}

export function removeKey(store: KeyValueStore, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    // nothing to clean up then
  }
}

function newLoginId(): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  } catch {
    // insecure context — fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Keys removed with the login (flags that belong to it). */
const LOGIN_SCOPED_KEYS = new Set<string>([LOGIN_ID_KEY]);

/** Register a key that is cleared at logout along with the login id. */
export function loginScopedKey(key: string): string {
  LOGIN_SCOPED_KEYS.add(key);
  return key;
}

/** Call at login: a fresh id, so once-per-login things happen again. */
export function startLoginSession(store: KeyValueStore = loginStore()): void {
  writeKey(store, LOGIN_ID_KEY, newLoginId());
}

/** Call at logout: the id and every login-scoped flag go. */
export function endLoginSession(store: KeyValueStore = loginStore()): void {
  for (const key of LOGIN_SCOPED_KEYS) removeKey(store, key);
}

/**
 * This login's id. A session that predates the id (logged in before this
 * shipped) gets one now, so it counts as one login from here on.
 */
export function currentLoginId(store: KeyValueStore = loginStore()): string {
  const existing = readKey(store, LOGIN_ID_KEY);
  if (existing) return existing;
  const id = newLoginId();
  writeKey(store, LOGIN_ID_KEY, id);
  return readKey(store, LOGIN_ID_KEY) ?? id;
}
